import { Globe, Settings, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { Switch } from "react-aria-components";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";
import { Dialog, DialogFooter } from "@/components/dialog";
import { Modal } from "@/components/modal";

import type { BriefMemory } from "../../hooks/useBriefMemory";
import type { AssetGroup } from "./types";

import { patchBrief } from "../../lib/brief-api";
import { requestIdFromSessionId } from "../../lib/session-ids";
import { AssetSection } from "./AssetSection";
import { AssetUploadBlock } from "./AssetUploadBlock";
import { emptyGroup, groupCount, readGroup } from "./types";

type UploadAssetsModalProps = {
  isOpen: boolean;
  /** The brief working memory — assets are read from and written back to it. */
  memory: BriefMemory | null;
  onOpenChange: (isOpen: boolean) => void;
  resourceId?: string;
  threadId?: string;
  /** Number of design variations in the brief. */
  variationCount: number;
};

export function UploadAssetsModal({
  isOpen,
  memory,
  onOpenChange,
  resourceId,
  threadId,
  variationCount,
}: UploadAssetsModalProps) {
  const canSeparate = variationCount >= 1;
  // The thread id already encodes the request id (D2) — no new prop needs
  // to reach the modal from outside.
  const requestId = threadId ? requestIdFromSessionId(threadId) : null;

  const [shared, setShared] = useState<AssetGroup>(emptyGroup);
  const [separate, setSeparate] = useState(false);
  const [modalVariations, setModalVariations] = useState<
    Array<{ variationNumber?: number }>
  >([]);
  const [perVariation, setPerVariation] = useState<
    Record<number | string, AssetGroup>
  >({});

  // Seed local state from the brief when the modal opens (not while editing, so
  // the live-save refetch doesn't clobber in-flight edits).
  useEffect(() => {
    if (!isOpen) return;

    const variations = Array.isArray(memory?.variations)
      ? memory.variations
      : [];

    const items = Array.from(
      { length: Math.max(1, variationCount, variations.length) },
      (_, index) => {
        const v = variations[index] as
          | { variationNumber?: unknown }
          | undefined;

        return {
          variationNumber:
            typeof v?.variationNumber === "number"
              ? v.variationNumber
              : undefined,
        };
      },
    );

    const initialPerVar: Record<number | string, AssetGroup> = {};

    for (const [index, item] of items.entries()) {
      const v = variations[index] as { assets?: unknown } | undefined;
      const key =
        item.variationNumber === undefined
          ? `fallback_${index}`
          : item.variationNumber;

      initialPerVar[key] = readGroup(v?.assets);
    }

    setShared(readGroup(memory?.assets));
    setSeparate(Boolean(memory?.assetsPerVariation) && canSeparate);
    setModalVariations(items);
    setPerVariation(initialPerVar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const persist = (patch: Record<string, unknown>) => {
    if (!threadId) return;
    void patchBrief(threadId, resourceId, patch);
  };

  const saveShared = (next: AssetGroup) => {
    setShared(next);
    persist({ assets: next });
  };

  const saveToggle = (next: boolean) => {
    setSeparate(next);
    persist({ assetsPerVariation: next });
  };

  const saveVariation = (
    variationNumber: number | undefined,
    fallbackIndex: number,
    next: AssetGroup,
  ) => {
    const key =
      variationNumber === undefined
        ? `fallback_${fallbackIndex}`
        : variationNumber;
    const updated = { ...perVariation, [key]: next };

    setPerVariation(updated);

    const base = Array.isArray(memory?.variations) ? memory.variations : [];
    const variations = base.map((variation, current) => {
      const vNum =
        typeof (variation as { variationNumber?: unknown })?.variationNumber ===
        "number"
          ? (variation as { variationNumber: number }).variationNumber
          : undefined;

      let assetsToUse: AssetGroup | undefined;

      if (vNum !== undefined) {
        if (vNum === variationNumber) {
          assetsToUse = next;
        } else if (vNum in updated) {
          assetsToUse = updated[vNum];
        }
      } else if (variationNumber === undefined && current === fallbackIndex) {
        assetsToUse = next;
      }

      return {
        ...(variation as object),
        assets:
          assetsToUse ?? readGroup((variation as { assets?: unknown })?.assets),
      };
    });

    persist({ variations });
  };

  const total =
    groupCount(shared) +
    (separate
      ? Object.values(perVariation).reduce(
          (sum, group) => sum + groupCount(group),
          0,
        )
      : 0);

  return (
    <Modal
      classNames={{ modal: "w-[1068px] max-w-[calc(100vw-48px)]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog className="flex h-[784px] max-h-[90vh] flex-col">
        <div className="flex items-start gap-lg px-3xl pt-3xl">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border-secondary text-text-primary">
            <Upload className="size-5" />
          </span>
          <div className="flex flex-col gap-xxs">
            <h2 className="text-lg font-semibold text-text-primary">
              Upload assets
            </h2>
            <p className="text-sm text-text-tertiary">
              By default your files are added to every variation. You can also
              add files to a specific variation.
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3xl py-xl">
          <div className="flex flex-col gap-lg">
            <AssetSection
              count={groupCount(shared)}
              highlight
              icon={<Globe className="size-5" />}
              subtitle="These assets are used in every variation."
              title="Used in all variations"
            >
              <AssetUploadBlock
                onChange={saveShared}
                requestId={requestId}
                value={shared}
              />
            </AssetSection>

            <div
              className={twMerge(
                "flex items-center gap-md rounded-xl border border-border-secondary p-lg",
                separate && "border-utility-brand-200 bg-utility-brand-50",
                !canSeparate && "opacity-60",
              )}
            >
              <Switch
                aria-label="Upload different assets for each variation"
                className="shrink-0 outline-hidden"
                isDisabled={!canSeparate}
                isSelected={separate}
                onChange={saveToggle}
              >
                {({ isSelected }) => (
                  <span
                    className={twMerge(
                      "flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full bg-bg-tertiary transition-colors",
                      isSelected && "bg-bg-brand-solid",
                    )}
                  >
                    <span
                      className={twMerge(
                        "size-4 translate-x-0.5 rounded-full bg-fg-white shadow-md transition-transform",
                        isSelected && "translate-x-[18px]",
                      )}
                    />
                  </span>
                )}
              </Switch>
              <div className="flex flex-col gap-xxs">
                <span className="text-sm font-semibold text-text-primary">
                  Upload different assets for each variation
                </span>
                <p className="text-xs text-text-tertiary">
                  {canSeparate
                    ? "Your assets will be used in all variations. Turn this on to add files and links to a specific variation."
                    : "Add design variations to the brief first to upload assets per variation."}
                </p>
              </div>
            </div>

            {separate && canSeparate
              ? modalVariations.map((item, index) => {
                  const key =
                    item.variationNumber === undefined
                      ? `fallback_${index}`
                      : item.variationNumber;
                  const group = perVariation[key] ?? emptyGroup();

                  return (
                    <AssetSection
                      count={groupCount(group)}
                      icon={<Settings className="size-5" />}
                      key={key}
                      subtitle="Files here are attached only to this variation"
                      title={`Variation ${index + 1}`}
                    >
                      <AssetUploadBlock
                        onChange={(next) =>
                          saveVariation(item.variationNumber, index, next)
                        }
                        requestId={requestId}
                        value={group}
                      />
                    </AssetSection>
                  );
                })
              : null}
          </div>
        </div>

        <DialogFooter className="border-t border-border-secondary shrink-0">
          <Button onPress={() => onOpenChange(false)} variant="secondary">
            Cancel
          </Button>
          <Button
            isDisabled={total === 0}
            onPress={() => onOpenChange(false)}
            variant="primary"
          >
            Upload assets
            <Upload className="size-4" />
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
